import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ReadinessGauge from "@/components/ReadinessGauge";
import { Trophy, KanbanSquare, DollarSign, ArrowRight, AlertCircle } from "lucide-react";

const fmt = (n) => "$" + Number(n || 0).toLocaleString();

export default function DashboardHome() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="h-64 flex items-center justify-center"><div className="h-8 w-8 border-[3px] border-slate-200 border-t-slate-900 rounded-full animate-spin" /></div>;

  const stats = [
    { label: "Win Probability Avg", value: data.avg_win_probability + "%", icon: Trophy, color: "text-accent" },
    { label: "Active Bids", value: data.active_bids, icon: KanbanSquare, color: "text-emerald-600" },
    { label: "Pipeline Value", value: fmt(data.pipeline_value), icon: DollarSign, color: "text-amber-600" },
  ];

  return (
    <div className="space-y-6" data-testid="dashboard-home">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">Welcome back, {user?.name?.split(" ")[0]}</h1>
        <p className="text-slate-500 mt-1">Here's where your contracting business stands today.</p>
      </div>

      {!data.profile_complete && (
        <div className="flex items-center justify-between gap-4 bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="profile-incomplete-banner">
          <div className="flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-amber-600 shrink-0" />
            <p className="text-sm text-amber-800">Complete your contractor profile to unlock accurate scores and matched opportunities.</p>
          </div>
          <button onClick={() => navigate("/dashboard/profile")} data-testid="complete-profile-button" className="shrink-0 bg-emerald-900 text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-emerald-800 transition-colors">Build Profile</button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 bg-white border border-slate-200 rounded-xl shadow-sm p-6 flex flex-col items-center justify-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 self-start mb-2">Contractor Readiness</p>
          <ReadinessGauge score={data.readiness.score} tier={data.readiness.tier} />
        </div>

        <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-6 content-start">
          {stats.map((s) => (
            <div key={s.label} className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 flex flex-col gap-2 h-fit" data-testid={`stat-${s.label.replace(/\s+/g, "-").toLowerCase()}`}>
              <s.icon className={`h-5 w-5 ${s.color}`} />
              <span className="font-data text-3xl font-medium tracking-tight text-slate-900">{s.value}</span>
              <span className="text-sm text-slate-500">{s.label}</span>
            </div>
          ))}
          <div className="sm:col-span-3 bg-emerald-900 text-white rounded-xl p-6 flex items-center justify-between">
            <div>
              <h3 className="font-heading text-lg font-semibold">Ready to find your next contract?</h3>
              <p className="text-slate-300 text-sm mt-1">Browse live opportunities matched to your trade and score your fit.</p>
            </div>
            <button onClick={() => navigate("/dashboard/opportunities")} data-testid="view-opportunities-button" className="shrink-0 bg-white text-slate-900 rounded-md px-5 py-2.5 text-sm font-medium hover:bg-slate-100 transition-colors inline-flex items-center gap-2">
              View <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6">
        <h3 className="font-heading text-lg font-semibold mb-4">Readiness Breakdown</h3>
        <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4">
          {Object.entries(data.readiness.breakdown).map(([k, v]) => (
            <div key={k} data-testid={`breakdown-${k.replace(/\s+/g, "-").toLowerCase()}`}>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-slate-700 font-medium">{k}</span>
                <span className="font-data text-slate-500">{v.score}/{v.max}</span>
              </div>
              <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                <div className="h-full rounded-full bg-accent" style={{ width: `${(v.score / v.max) * 100}%` }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
